import { DatePipe } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  pairwise,
  startWith,
} from 'rxjs';
import { ActiveFilterChip } from '../../../models';
import {
  CheckboxFieldComponent,
  DatepickerFieldComponent,
  InputFieldComponent,
  Item,
  MultiSelectFieldComponent,
  RadioFieldComponent,
  SelectFieldComponent,
  SelectTipologicaFieldComponent,
  SwitchFieldComponent,
} from '../../form';
import { FilterFieldConfig } from '../models/search-filters.model';

@UntilDestroy()
@Component({
  imports: [
    MatChipRow,
    MatChipRemove,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    SelectTipologicaFieldComponent,
    CheckboxFieldComponent,
    DatepickerFieldComponent,
    MultiSelectFieldComponent,
    MatBadgeModule,
    RadioFieldComponent,
    SwitchFieldComponent,
    TranslatePipe,
    MatSelectModule,
  ],

  selector: 'siat-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent implements OnInit {
  /**
   * The main filters form group.
   */
  filterFormGroup = input.required<FormGroup>();

  /**
   * The configuration for filter items.
   */
  filterItems = input<FilterFieldConfig[]>([]);

  /**
   * The minimum length of the search string to trigger a search. Default is 3.
   */
  minLengh = input<number>(3);

  /**
   * Placeholder for the global search input field.
   */
  inputSearchPlaceholder? = input<string>(null);

  /**
   * The name of the global search control in the form group. Default is 'globalSearch'.
   */
  inputSearchName? = input<string>('globalSearch');

  tableReference? = input<string>('tableReference');

  /**
   * Injected date pipe for formatting date values in filter chips.
   */
  datePipe = inject(DatePipe);

  /**
   * Emits when a search should be triggered.
   */
  onSearch = output<void>();

  /**
   * Emits when all filters are reset.
   */
  onReset = output<void>();

  /**
   * Controls the visibility of the filter sheet.
   */
  isFiltriVisible = signal(false);

  /**
   * Stores the filter values only after the user clicks "Apply" or "Search".
   */
  private appliedFiltersState: Record<string, unknown> = {};

  /**
   * Indicates if any of the filter fields is a select input, used to conditionally display the "Apply" button.
   */
  areSelectInput = input<boolean>(false);

  /**
   * Options for select-type filters, if any. This is used to populate select inputs in the filter sheet.
   */
  selectOptions = input<Item[]>([]);

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.setupGlobalSearchListener();
    this.setupAdvancedFiltersListener();
  }

  /**
   * Toggles the visibility of the filter sheet.
   */
  toggleFiltri(): void {
    this.isFiltriVisible.update(visible => !visible);
  }

  /**
   * Closes the filter sheet.
   */
  closeFiltri(): void {
    this.isFiltriVisible.set(false);
  }

  /**
   * Triggers a search, captures the current filter state for display,
   * closes the filter panel, and emits the onSearch event.
   */
  triggerSearch(): void {
    const filtered: Record<string, unknown> = {};

    for (const key in this.filterFormGroup().value) {
      const value = this.filterFormGroup().value[key];

      if (value !== null && value !== undefined) {
        filtered[key] = value;
      }
    }

    Object.keys(this.filterFormGroup().controls).forEach(key => {
      const control = this.filterFormGroup().get(key);
      if (control && typeof control.value === 'string') {
        control.setValue(control.value, { emitEvent: false });
      }
    });

    this.appliedFiltersState = filtered;

    const globalSearchValue = this.filterFormGroup().get(
      this.inputSearchName()
    )?.value;
    const isEmptySearch = !globalSearchValue || globalSearchValue.length === 0;

    if (!isEmptySearch && !this.filterFormGroup().valid) return;

    this.closeFiltri();
    this.onSearch.emit();
  }

  /**
   * Resets all filters, including the applied state, closes the panel,
   * emits the onReset event and triggers a new search.
   */
  resetAllFilters(): void {
    this.filterFormGroup().reset({}, { emitEvent: true });
    this.filterFormGroup()
      .get(this.inputSearchName())
      ?.enable({ emitEvent: true });
    this.closeFiltri();
    this.appliedFiltersState = {};
    this.onReset.emit();
  }

  /**
   * Removes a single advanced filter and triggers a new search to update the UI.
   * @param filterName The name of the control to reset.
   */
  removeFilter(filterName: string): void {
    const control = this.filterFormGroup().get(filterName);
    if (control) {
      control.reset();
    }

    this.appliedFiltersState = this.filterFormGroup().value;
    this.onSearch.emit();
  }

  /**
   * A computed property that returns an array of active advanced filters
   * based on the last applied search state.
   */
  get activeFilters(): ActiveFilterChip[] {
    const formValue = this.appliedFiltersState;
    return (
      this.filterItems()
        .map(field => {
          const value = formValue[field.controlName];
          if (value === null || value === undefined || value === '')
            return null;

          let displayValue: string;
          if (Array.isArray(value)) {
            if (value.length === 0) return null;
            displayValue = value
              .map((item: { name?: string }) => item?.name ?? item)
              .join(', ');
          } else if (
            typeof value === 'object' &&
            (value as { name?: string })?.name
          ) {
            displayValue = (value as { name: string }).name;
          } else if (typeof value === 'boolean') {
            displayValue = value ? 'Sì' : 'No';
          } else {
            displayValue = String(value);
          }

          return {
            key: field.controlName,
            label: field.label,
            value: displayValue,
          } as ActiveFilterChip;
        })
        .filter((chip): chip is ActiveFilterChip => chip !== null) ?? []
    );
  }

  /**
   * A computed property that checks if any filter is currently active in the form.
   * This is used to enable/disable the reset button.
   */
  get hasActiveFilters(): boolean {
    const formValues = this.filterFormGroup()?.value;
    if (!formValues) return false;
    return Object.values(formValues).some(
      value => value !== null && value !== undefined && value !== ''
    );
  }

  /**
   * Sets up the listener for the global search input field.
   */
  private setupGlobalSearchListener(): void {
    const searchStringFc = this.filterFormGroup().get(this.inputSearchName());

    if (!searchStringFc) return;
    searchStringFc.valueChanges
      .pipe(
        debounceTime(400),
        startWith(searchStringFc.value || ''),
        pairwise(),
        untilDestroyed(this)
      )
      .subscribe(([previousValue, currentValue]) => {
        const previousSearch = previousValue ?? '';
        const currentSearch = currentValue ?? '';
        const currentLength = currentSearch.length;
        const minLength = this.minLengh();
        const isDeleting = currentLength < previousSearch.length;

        if (
          currentLength === 0 ||
          currentLength >= minLength ||
          isDeleting ||
          typeof currentSearch === 'object'
        ) {
          this.triggerSearch();
        }
      });
  }

  /**
   * Sets up a listener for advanced filters to disable/enable the global search.
   */
  private setupAdvancedFiltersListener(): void {
    const advancedFilterControls = this.filterItems()
      .filter(f => f.controlName !== 'globalSearch')
      .map(f => f.controlName);

    this.filterFormGroup()
      .valueChanges.pipe(
        startWith(this.filterFormGroup().value),
        map(formValue =>
          advancedFilterControls.some(
            controlName =>
              formValue[controlName] !== null &&
              formValue[controlName] !== undefined &&
              formValue[controlName] !== ''
          )
        ),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe(hasAdvancedFilters => {
        const globalSearch = this.filterFormGroup().get('globalSearch');
        if (hasAdvancedFilters) {
          globalSearch?.reset('', { emitEvent: false });
          globalSearch?.disable({ emitEvent: false });
        } else {
          globalSearch?.enable({ emitEvent: false });
        }
      });
  }

  transformFilterVelue(filter: ActiveFilterChip) {
    const fieldConfig = this.filterItems().find(
      item => item.controlName === filter.key
    );
    if (!fieldConfig) return filter.value;

    switch (fieldConfig.type) {
      case 'date': {
        const dateValue = new Date(filter.value);
        return this.datePipe.transform(dateValue + 'Z', 'dd/MM/yyyy') || '';
      }

      default:
        return filter.value;
    }
  }
}
